import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, Checkbox, message, List, Tag, Typography, Space, Descriptions, Select, Button } from 'antd';
import { ShopOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import {
  createShipment, createCustomerAddress, findDuplicateCustomerAddress, searchCustomers,
} from '../../../services/logistics';
import type {
  ShippingRateQuoteResult, FullAddressPayload, PackagePurchaseResult, CustomerOption,
} from '../../../services/logistics';
import type { QuoteOverview, PackageOriginInfo } from './ParcelRateQuote';
import { formatMoney, formatWeightBreakdown } from './units';
import { useLabelPreview } from '../shared/useLabelPreview';

const { Text } = Typography;

const BATTERY_CHEMISTRY_LABELS: Record<string, string> = {
  lithium_ion: '锂离子',
  lithium_metal: '锂金属',
};
const BATTERY_INSTALLATION_LABELS: Record<string, string> = {
  installed: '安装在设备内',
  packed: '随设备未安装',
  standalone: '单独电池',
};

interface PurchaseFormValues {
  remarks?: string;
  saveOriginToAddressBook?: boolean;
  originAddressLabel?: string;
  saveDestinationToAddressBook?: boolean;
  destinationAddressLabel?: string;
}

interface PurchaseModalProps {
  open: boolean;
  quote: ShippingRateQuoteResult | null;
  /** 报价阶段已经声明过的签名/保险/电池信息，购买阶段只读展示，不再重新询问 */
  signatureRequired?: boolean;
  insuranceAmount?: number;
  batteryDeclared?: boolean;
  batteryChemistry?: 'lithium_ion' | 'lithium_metal';
  batteryInstallation?: 'installed' | 'packed' | 'standalone';
  /** 报价阶段已经锁定的完整地址——购买弹窗只读展示、不能修改，避免报价用一个地址、
   * 购买时又变成另一个地址，导致偏远地区附加费等和邮编/地址挂钩的费用跟报价对不上。 */
  origin?: Partial<FullAddressPayload>;
  destination?: Partial<FullAddressPayload>;
  /** 询价概览（包裹数量/总重量等），用来拼弹窗标题 */
  overview?: QuoteOverview | null;
  /** 每个包裹对应询价时第几个包裹类型、那个类型的重量/尺寸——和 quote.rate_quote_ids
   * 严格同序、等长，购买结果按下标对应即可 */
  packageOrigins?: PackageOriginInfo[];
  /** 询价表单"其他信息"里已经填过的备注，购买弹窗预填这个值，但仍然可以在这里改 */
  remarks?: string;
  /** 员工/管理员如果在询价阶段就已经指定了客户，购买弹窗直接带出同一个客户，不需要
   * 再选一遍——客户账号自己购买、或者询价时没指定客户，这里是 undefined，走原来的
   * 手动选择流程。 */
  initialCustomer?: CustomerOption;
  onClose: () => void;
  onSuccess: () => void;
  /** 购买成功后点"创建新运单"：关闭这个弹窗，同时通知父组件解锁询价表单、清空上一轮报价结果，
   * 让用户可以直接在已经填好的地址基础上改一改再查一次——复用的就是"修改询价信息"那套逻辑。 */
  onCreateNew: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  open,
  quote,
  signatureRequired,
  insuranceAmount,
  batteryDeclared,
  batteryChemistry,
  batteryInstallation,
  origin,
  destination,
  overview,
  packageOrigins,
  remarks,
  initialCustomer,
  onClose,
  onSuccess,
  onCreateNew,
}) => {
  const [form] = Form.useForm<PurchaseFormValues>();
  const saveOriginToAddressBook = Form.useWatch('saveOriginToAddressBook', form);
  const saveDestinationToAddressBook = Form.useWatch('saveDestinationToAddressBook', form);
  const [submitting, setSubmitting] = useState(false);
  const [purchaseResults, setPurchaseResults] = useState<PackagePurchaseResult[] | null>(null);
  const { openLabel, closePreview, mergeAndPreview, merging, modal: labelPreviewModal } = useLabelPreview();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  // 物流记录页面对三种角色注册的是三个不同的 URL（管理员/员工/客户端），不是同一个路径，
  // 要按当前登录角色跳到对应的那一个。
  const shipmentsListPath = `/dashboard/${user?.role || 'customer'}/logistics/shipments`;
  const handleViewShipmentsList = () => {
    handleClose();
    navigate(shipmentsListPath);
  };
  // 管理员/员工代客户购买运单必须指定客户（后端 create_shipment 会强制要求），
  // 客户账号自己购买不需要选，后端会按登录身份自动解析
  const requiresCustomerSelect = !isCustomer;
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);

  // 询价阶段已经指定过客户时（initialCustomer），弹窗打开时直接带出这个客户，不用再选
  // 一遍——只在"弹窗从关到开"这个瞬间做一次，避免用户在弹窗里自己改了选择之后，因为
  // 组件没有卸载重建，又被这个 effect 覆盖回询价阶段的旧值。
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current && initialCustomer) {
      setSelectedCustomerId(initialCustomer.id);
      setCustomerOptions((prev) => (prev.some((c) => c.id === initialCustomer.id) ? prev : [initialCustomer, ...prev]));
    }
    prevOpenRef.current = open;
  }, [open, initialCustomer]);
  const customerSearchTimerRef = useRef<number | null>(null);

  const handleCustomerSearch = (keyword: string) => {
    if (customerSearchTimerRef.current) {
      window.clearTimeout(customerSearchTimerRef.current);
    }
    customerSearchTimerRef.current = window.setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const results = await searchCustomers(keyword);
        setCustomerOptions(results);
      } catch (error) {
        console.error('查询客户列表失败:', error);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
  };

  // 购买成功后，按用户勾选把发件人/收件人地址顺手存进地址库；先查重，已存在就跳过，
  // 避免同一个联系人/地址被反复创建。这一步失败不影响运单已经购买成功这件事，只弹提示。
  // 员工/管理员代客户购买时，customer 传的是购买时已经选定的那个客户主账号（selectedCustomerId），
  // 不需要再单独选一次——不选定客户根本没法完成购买，走到这一步时这个值必然已经有了。
  const saveAddressToBookIfNeeded = async (
    address: FullAddressPayload,
    addressType: 'sender' | 'recipient',
    sideLabel: string,
    label: string | undefined,
  ) => {
    const targetCustomerId = requiresCustomerSelect ? selectedCustomerId : undefined;
    try {
      const dupResp = await findDuplicateCustomerAddress({
        name: address.name,
        phone: address.phone,
        address_line: address.address_line,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: address.country,
        customer: targetCustomerId,
      });
      if (dupResp.data) {
        message.info(`${sideLabel}地址已存在于地址库，未重复创建`);
        return;
      }
      await createCustomerAddress({
        customer: targetCustomerId,
        label: label || undefined,
        address_type: addressType,
        country: address.country,
        postal_code: address.postal_code,
        state: address.state,
        city: address.city,
        address_line: address.address_line,
        address_line2: address.address_line2 || '',
        name: address.name,
        company: address.company || '',
        phone: address.phone,
        email: address.email || '',
        is_active: true,
      });
      message.success(`已将${sideLabel}地址保存到地址库`);
    } catch (error) {
      console.error(`保存${sideLabel}地址到地址库失败:`, error);
      message.warning(`保存${sideLabel}地址到地址库失败`);
    }
  };

  const handleCopyTrackingNumber = async (trackingNumber: string) => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  // 文件名用承运商代码（如 USPS），不用中文名（如"美国邮政"）
  const handleViewAllLabels = () => {
    if (!purchaseResults) return;
    mergeAndPreview(purchaseResults, quote?.carrier_code || quote?.carrier_name || '运单');
  };

  const handleCopyAllInfo = async () => {
    if (!purchaseResults) return;
    const carrierLabel = quote ? `${quote.carrier_name} ${quote.service_name}` : '';
    const lines = purchaseResults.map((item, index) => {
      const packageOrigin = packageOrigins?.[index];
      const typeLabel = packageOrigin
        ? `包裹类型${packageOrigin.typeIndex + 1} ${packageOrigin.weightLabel} ${packageOrigin.dimsLabel}`
        : '';
      const resultLabel = item.success ? `运单号：${item.tracking_number}` : `购买失败：${item.error}`;
      return [`序号#${index + 1}`, typeLabel, carrierLabel, resultLabel].filter(Boolean).join(' ');
    });
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      message.success('已复制');
    } catch {
      message.error('复制失败');
    }
  };

  const handleClose = () => {
    setPurchaseResults(null);
    setSelectedCustomerId(undefined);
    setCustomerOptions([]);
    closePreview();
    form.resetFields();
    onClose();
  };

  const handleCreateNew = () => {
    setPurchaseResults(null);
    setSelectedCustomerId(undefined);
    setCustomerOptions([]);
    closePreview();
    form.resetFields();
    onCreateNew();
  };

  const handleOk = async () => {
    if (!quote || !origin || !destination) return;
    if (purchaseResults) {
      // 已经买过一次了，Modal 处于结果展示状态，再次点确定视为关闭
      handleClose();
      onSuccess();
      return;
    }
    if (requiresCustomerSelect && !selectedCustomerId) {
      Modal.error({ title: '购买运单失败', content: '请先选择所属客户', centered: true });
      return;
    }
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const response = await createShipment({
        rate_quote_ids: quote.rate_quote_ids,
        origin: origin as FullAddressPayload,
        destination: destination as FullAddressPayload,
        remarks: values.remarks,
        signature_required: signatureRequired,
        insurance_amount: insuranceAmount,
        battery_declared: batteryDeclared,
        battery_chemistry: batteryDeclared ? batteryChemistry : undefined,
        battery_installation: batteryDeclared ? batteryInstallation : undefined,
        customer_id: requiresCustomerSelect ? selectedCustomerId : undefined,
      });
      setPurchaseResults(response.data.results);
      const successCount = response.data.results.filter((r) => r.success).length;
      const total = response.data.results.length;
      if (successCount === total) {
        message.success(`购买成功，共 ${total} 个包裹`);
      } else {
        message.warning(`${successCount}/${total} 个包裹购买成功，请查看下方详情`);
      }
      // 只要至少买成功一个包裹，就说明这两个地址确实是有效可用的，才顺带存地址库；
      // 保存动作本身不阻塞、不影响上面已经展示的购买结果。客户账号、员工/管理员代客户
      // 购买都可以勾选保存，区别只在于存进谁的地址库（见 saveAddressToBookIfNeeded 里的判断）。
      if (successCount > 0) {
        if (values.saveOriginToAddressBook) {
          saveAddressToBookIfNeeded(origin as FullAddressPayload, 'sender', '发件人', values.originAddressLabel);
        }
        if (values.saveDestinationToAddressBook) {
          saveAddressToBookIfNeeded(destination as FullAddressPayload, 'recipient', '收件人', values.destinationAddressLabel);
        }
      }
    } catch (error: any) {
      if (error?.errorFields) return; // antd 表单校验失败，不需要额外提示
      const detail = error?.response?.data?.detail;
      Modal.error({ title: '购买运单失败', content: detail || '购买运单失败，请重试或联系客服', centered: true });
      console.error('购买运单失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderAddressSummary = (addr?: Partial<FullAddressPayload>) => (
    <Descriptions bordered size="small" column={2} style={{ marginBottom: 12 }}>
      <Descriptions.Item label="姓名/公司">{addr?.name || '-'}{addr?.company ? `（${addr.company}）` : ''}</Descriptions.Item>
      <Descriptions.Item label="电话">{addr?.phone || '-'}</Descriptions.Item>
      <Descriptions.Item label="地址" span={2}>
        {addr?.address_line || '-'}{addr?.address_line2 ? `，${addr.address_line2}` : ''}
      </Descriptions.Item>
      <Descriptions.Item label="城市 州 邮编" span={2}>
        {[addr?.city, addr?.state, addr?.postal_code].filter(Boolean).join(' ') || '-'}
      </Descriptions.Item>
      <Descriptions.Item label="国家 (地区)" span={2}>{addr?.country || '-'}</Descriptions.Item>
    </Descriptions>
  );

  const buildTitle = () => {
    if (!quote) return '购买运单';
    if (!overview) return `购买运单 - ${quote.carrier_name} ${quote.service_name}`;
    const weightText = formatWeightBreakdown(overview.totalWeightDisplay, overview.overviewUnitSystem).replace(/\s+/g, '');
    return (
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <span>购买运单</span>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              display: 'flex', gap: 28, fontWeight: 'normal',
              border: '1px solid #d9d9d9', borderRadius: 4, padding: '4px 16px',
            }}
          >
            <span>{overview.originCountry}({overview.originPostalCode}) 至 {overview.destinationCountry}({overview.destinationPostalCode})</span>
            <span>共{overview.packageCount}个包裹</span>
            <span>共{weightText}</span>
          </div>
        </div>
      </div>
    );
  };

  const transitText = (() => {
    if (!quote || quote.transit_days_min == null) return '-';
    if (quote.transit_days_max != null && quote.transit_days_max !== quote.transit_days_min) {
      return `${quote.transit_days_min}-${quote.transit_days_max} 天`;
    }
    return `${quote.transit_days_min} 天`;
  })();

  const totalWithDeposit = quote
    ? (quote.deposit_amount == null ? Number(quote.price) : Number(quote.price) + Number(quote.deposit_amount))
    : 0;

  return (
    <>
    <Modal
      title={buildTitle()}
      open={open}
      onCancel={handleClose}
      width={800}
      destroyOnClose
      footer={purchaseResults ? (
        <Space>
          <Button onClick={handleViewShipmentsList}>运单列表</Button>
          <Button onClick={handleCreateNew}>创建新运单</Button>
          <Button type="primary" onClick={() => { handleClose(); onSuccess(); }}>关闭</Button>
        </Space>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {requiresCustomerSelect && (
              <Select
                showSearch
                allowClear
                placeholder="所属客户（输入编码或名称搜索）"
                style={{ width: 280 }}
                value={selectedCustomerId}
                onChange={(value) => setSelectedCustomerId(value)}
                onSearch={handleCustomerSearch}
                onDropdownVisibleChange={(open) => { if (open) handleCustomerSearch(''); }}
                filterOption={false}
                loading={customerSearchLoading}
                notFoundContent={customerSearchLoading ? '搜索中...' : '未找到匹配的客户'}
                options={customerOptions.map((c) => ({
                  value: c.id,
                  label: `${c.customer_code || '-'} ${c.company_name || c.username}`,
                }))}
              />
            )}
          </div>
          <Space>
            <Button onClick={handleClose}>取消</Button>
            <Button
              type="primary"
              loading={submitting}
              disabled={requiresCustomerSelect && !selectedCustomerId}
              onClick={handleOk}
            >
              确认购买
            </Button>
          </Space>
        </div>
      )}
    >
      {purchaseResults ? (
        <>
          <List
            dataSource={purchaseResults}
            renderItem={(item, index) => {
              const packageOrigin = packageOrigins?.[index];
              return (
                <List.Item>
                  <Space align="start" size={12} style={{ width: '100%' }}>
                    <div
                      style={{
                        width: 40, height: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2,
                      }}
                    >
                      {quote?.carrier_logo ? (
                        <img src={quote.carrier_logo} alt={quote.carrier_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      ) : (
                        <ShopOutlined style={{ color: '#bfbfbf', fontSize: 22 }} />
                      )}
                    </div>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <div>
                        <Space size={12}>
                          <Text strong>序号#{index + 1}</Text>
                          {packageOrigin && (
                            <>
                              <Text strong>包裹类型{packageOrigin.typeIndex + 1}</Text>
                              <Text strong>{packageOrigin.weightLabel}</Text>
                              <Text strong>{packageOrigin.dimsLabel}</Text>
                            </>
                          )}
                          {item.success ? <Tag color="success">购买成功</Tag> : <Tag color="error">购买失败</Tag>}
                        </Space>
                      </div>
                      {item.success ? (
                        <>
                          <Space size={4}>
                            <Text>
                              {quote?.carrier_name} {quote?.service_name} 运单号：{item.tracking_number}
                            </Text>
                            {item.tracking_number && (
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyTrackingNumber(item.tracking_number!)}
                              />
                            )}
                          </Space>
                          {item.label_url && (
                            <Button
                              type="primary"
                              size="large"
                              style={{
                                marginTop: 6,
                                height: 52,
                                paddingInline: 32,
                                fontSize: 18,
                                fontWeight: 600,
                                backgroundColor: '#52c41a',
                                borderColor: '#52c41a',
                              }}
                              onClick={() => openLabel(item.label_url as string, item.tracking_number)}
                            >
                              打印运单
                            </Button>
                          )}
                          {item.error && <Text type="warning">{item.error}</Text>}
                        </>
                      ) : (
                        <Text type="danger">{item.error}</Text>
                      )}
                    </Space>
                  </Space>
                </List.Item>
              );
            }}
          />
          <Space style={{ marginTop: 12 }}>
            {purchaseResults.length > 1 && (
              <Button loading={merging} onClick={handleViewAllLabels}>
                查看/打印所有运单
              </Button>
            )}
            <Button icon={<CopyOutlined />} onClick={handleCopyAllInfo}>
              复制所有信息
            </Button>
          </Space>
        </>
      ) : (
        <>
          <Form<PurchaseFormValues> form={form} layout="vertical" initialValues={{ remarks }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>发货地址</h4>
              <Form.Item name="saveOriginToAddressBook" valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                <Checkbox>添加到地址库</Checkbox>
              </Form.Item>
            </div>
            {renderAddressSummary(origin)}
            {saveOriginToAddressBook && (
              <Form.Item name="originAddressLabel" style={{ marginBottom: 12 }}>
                <Input placeholder="给这个地址起个备注名（如：总部仓库），不填则默认用姓名" />
              </Form.Item>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>收货地址</h4>
              <Form.Item name="saveDestinationToAddressBook" valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                <Checkbox>添加到地址库</Checkbox>
              </Form.Item>
            </div>
            {renderAddressSummary(destination)}
            {saveDestinationToAddressBook && (
              <Form.Item name="destinationAddressLabel" style={{ marginBottom: 12 }}>
                <Input placeholder="给这个地址起个备注名（如：常用收件人-张三），不填则默认用姓名" />
              </Form.Item>
            )}
            <h4>其他信息</h4>
            <Descriptions bordered size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="签名服务">{signatureRequired ? '需要' : '不需要'}</Descriptions.Item>
              <Descriptions.Item label="保险">
                {insuranceAmount ? `需要（保额 ${insuranceAmount} USD）` : '不需要'}
              </Descriptions.Item>
              <Descriptions.Item label="电池">
                {batteryDeclared
                  ? `含电池（${BATTERY_CHEMISTRY_LABELS[batteryChemistry!] || ''}-${BATTERY_INSTALLATION_LABELS[batteryInstallation!] || ''}）`
                  : '不含电池'}
              </Descriptions.Item>
            </Descriptions>
            <Form.Item label="发货备注" name="remarks">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>

          {quote && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, minWidth: 640, padding: '4px 2px' }}>
                <Space size={12}>
                  <div
                    style={{
                      width: 56, height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {quote.carrier_logo ? (
                      <img src={quote.carrier_logo} alt={quote.carrier_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <ShopOutlined style={{ color: '#bfbfbf', fontSize: 28 }} />
                    )}
                  </div>
                  <div>
                    <div><Text strong style={{ fontSize: 18 }}>{quote.carrier_name}</Text></div>
                    <div><Text type="secondary" style={{ fontSize: 16 }}>{quote.service_name}</Text></div>
                  </div>
                </Space>
                <div style={{ whiteSpace: 'nowrap' }}>
                  <div><Text type="secondary" style={{ fontSize: 14 }}>预计时效</Text></div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{transitText}</div>
                </div>
                <div style={{ background: '#f0f5ff', borderRadius: 8, padding: '10px 20px', whiteSpace: 'nowrap' }}>
                  <div>运单价格 {formatMoney(quote.price, quote.currency)}</div>
                  <div>
                    风险保证金(可退) {quote.deposit_amount == null ? '-' : formatMoney(quote.deposit_amount, quote.currency)}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                    总费用 {formatMoney(totalWithDeposit, quote.currency)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
    {labelPreviewModal}
    </>
  );
};

export default PurchaseModal;
